import { useEffect, useState } from "react";
import { Users, BriefcaseBusiness, CircleDollarSign, AlertTriangle } from "lucide-react";
import axiosInstance from "../config/config";

export default function DashboardPage() {
  const [stats, setStats] = useState({
    clients: 0,
    jobs: 0,
    expenses: 0,
    pendingItems: 0,
  });
  const [outstandingItems, setOutstandingItems] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [clientsRes, jobsRes, expensesRes, pendingRes] = await Promise.all([
          axiosInstance.post("", {
            SysID: `select count(*) as total from dbo.job_client;`,
          }),
          axiosInstance.post("", {
            SysID: `select count(*) as total from dbo.job;`,
          }),
          axiosInstance.post("", {
            SysID: `select isnull(sum(amount), 0) as total from dbo.job_expense;`,
          }),
          axiosInstance.post("", {
            SysID: `select count(*) as total from dbo.job_job_item where is_returned=0;`,
          }),
        ]);
        setStats({
          clients: clientsRes.data[0]?.total || 0,
          jobs: jobsRes.data[0]?.total || 0,
          expenses: expensesRes.data[0]?.total || 0,
          pendingItems: pendingRes.data[0]?.total || 0,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    const fetchOutstanding = async () => {
      try {
        const response = await axiosInstance.post("", {
          SysID: `select jji.id, jji.item_name, jji.qty, j.j_name, j.c_name from dbo.job_job_item jji join dbo.job j on jji.job_id = j.id where jji.is_returned = 0 order by j.j_name;`,
        });
        setOutstandingItems(response.data || []);
      } catch (error) {
        console.error("Error fetching outstanding items:", error);
      }
    };

    fetchStats();
    fetchOutstanding();
  }, []);

  const cards = [
    {
      label: "Total Clients",
      value: stats.clients,
      icon: Users,
      colorClass: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    },
    {
      label: "Total Jobs",
      value: stats.jobs,
      icon: BriefcaseBusiness,
      colorClass: "bg-purple-500/20 text-purple-300 border-purple-500/30",
    },
    {
      label: "Total Expenses (Rs.)",
      value: parseFloat(stats.expenses).toFixed(2),
      icon: CircleDollarSign,
      colorClass: "bg-green-500/20 text-green-300 border-green-500/30",
    },
    {
      label: "Pending Returns",
      value: stats.pendingItems,
      icon: AlertTriangle,
      colorClass:
        stats.pendingItems > 0
          ? "bg-red-500/20 text-red-300 border-red-500/30"
          : "bg-green-500/20 text-green-300 border-green-500/30",
    },
  ];

  return (
    <div className="h-full bg-slate-700 p-4 sm:p-8 overflow-auto">
      <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6">
        Dashboard
      </h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`rounded-xl p-4 sm:p-5 border ${card.colorClass} backdrop-blur`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs sm:text-sm opacity-80">{card.label}</p>
                <Icon size={18} />
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* Outstanding Items */}
      <div className="bg-white/10 rounded-xl p-4 sm:p-5 text-white">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={20} className="text-yellow-400" />
          <h2 className="text-lg font-semibold">Items Not Returned</h2>
          <span
            className={`ml-auto text-white text-xs px-2 py-0.5 rounded-full ${
              outstandingItems.length > 0 ? "bg-red-500" : "bg-green-500"
            }`}
          >
            {outstandingItems.length}
          </span>
        </div>

        {outstandingItems.length === 0 ? (
          <p className="text-gray-400 text-center py-6">
            All items have been returned!
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/20 text-left text-gray-300">
                  <th className="pb-2 pr-4">Item</th>
                  <th className="pb-2 pr-4">Qty</th>
                  <th className="pb-2 pr-4">Job</th>
                  <th className="pb-2">Client</th>
                </tr>
              </thead>
              <tbody>
                {outstandingItems.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-white/10 hover:bg-white/5"
                  >
                    <td className="py-2 pr-4 font-medium text-yellow-300">
                      {item.item_name}
                    </td>
                    <td className="py-2 pr-4">x{item.qty}</td>
                    <td className="py-2 pr-4 text-gray-300">{item.j_name}</td>
                    <td className="py-2 text-gray-300">{item.c_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
